// ** React Imports
import { Link, useHistory } from 'react-router-dom'
import { useIntl } from "react-intl";
import { useContext, useEffect, useState } from "react";
// ** Custom Components
import Avatar from '@components/avatar'
import Loading from "@views/common/Loading"

// ** oidc
import { useReactOidc } from "@axa-fr/react-oidc-context"

// ** Third Party Components
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label, Row, Col, CustomInput } from 'reactstrap'
import { Power, RefreshCw, AlertTriangle, Key } from 'react-feather'

// ** Default Avatar Image
import defaultAvatar from '@src/assets/images/avatars/avatar-blank.png'
import { IntlContext } from "@src/utility/context/Internationalization"
import { getFile } from "@services/FileService";
import { findPersonInfo } from "@services/PersonService";
import { changePwd, activeSmartOTP, findDivices, deleteDevices } from '@services/PersonService';
import Notification from "@views/common/Notification"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye } from '@fortawesome/free-solid-svg-icons'
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons'

const UserDropdown = () => {
  const { oidcUser, logout } = useReactOidc()
  const intlContext = useContext(IntlContext)
  const intl = useIntl()
  const headers = {
    Authorization: `Bearer ${oidcUser && oidcUser.access_token}`,
    "Accept-Language": intlContext.locale
  }
  const [avatar, setAvatar] = useState(null)
  const [centeredModal, setCenteredModal] = useState(false)
  const [success, setSuccess] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pwdOld, setPwdOld] = useState('')
  const [pwdNew, setPwdNew] = useState('')
  const [pwdConfirm, setPwdConfirm] = useState('')
  const [fetching, setFetching] = useState(true)
  const [person, setPerson] = useState({})
  const [modalOTP, setModalOTP] = useState(false)
  const [devices, setDevices] = useState([])
  const eye = <FontAwesomeIcon icon={faEye} />
  const eyeSlash = <FontAwesomeIcon icon={faEyeSlash} />

  useEffect(() => {
    if (fetching) {
      findPersonInfo(headers)
        .then((res) => {
          setPerson(res?.data)
          if (res) {
            if (res.data?.avatarId) {
              getFile(headers, res.data.avatarId)
                .then((res) => {
                  if (res) {
                    const blob = new Blob([res.data], { type: res.contentType })
                    const avatarUrl = URL.createObjectURL(blob)
                    setAvatar(avatarUrl)
                  }
                })
            }
          }
        })
        .catch((err) => {
          setError(err)
        })
        .finally(() => {
          setFetching(false)
          setSuccess(null)
          setError(null)
          setLoading(false)
        })
    }
  }, [fetching])

  const changePassword = () => {
    setLoading(true)
    changePwd(headers, {
      passwordOld: pwdOld,
      passwordNew: pwdNew,
      passwordConfirm: pwdConfirm
    })
      .then((res) => {
        setSuccess(res.data)
        setTimeout(() => {
          logout()
        }, 2000)
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setSuccess(null)
        setError(null)
        setLoading(false)
      })
  }

  const changePwdValues = (values, label) => {
    if (label === 'pwdOld') {
      setPwdOld(values)
    } else if (label === 'pwdNew') {
      setPwdNew(values)
    } else {
      setPwdConfirm(values)
    }
  }

  const [passwordOldShown, setPasswordOldShown] = useState(false)
  const [passwordNewShown, setPasswordNewShown] = useState(false)
  const [passwordConfirmShown, setPasswordConfirmShown] = useState(false)

  const togglePasswordOldVisiblity = () => {
    setPasswordOldShown(passwordOldShown ? false : true);
  }

  const togglePasswordNewVisiblity = () => {
    setPasswordNewShown(passwordNewShown ? false : true);
  }

  const togglePasswordConfirmVisiblity = () => {
    setPasswordConfirmShown(passwordConfirmShown ? false : true);
  }

  const activeOtp = () => {
    setLoading(true)
    activeSmartOTP(headers, person?.id)
      .then((res) => {
        window.location.replace(`${process.env.REACT_APP_SMART_OTP_LINK}`)
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setSuccess(null)
        setError(null)
        setLoading(false)
        setFetching(true)
      })
  }

  useEffect(() => {
    if (person?.id) {
      findDivices(headers, person?.id)
        .then((res) => {
          setDevices(res?.data)
        })
    }
  }, [person])

  const deleteDevice = (value) => {
    deleteDevices(headers, person?.id, {
      credentialsId: value
    }).then((res) => {
      setSuccess(intl.formatMessage({ id: "deleteDevice" }))
    })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setSuccess(null)
        setError(null)
        setLoading(false)
        setFetching(true)
      })
  }

  const openModal = () => {
    setModalOTP(true)
    setFetching(true)
  }

  const closeModal = () => {
    setPerson({})
    setModalOTP(false)
  }

  return (
    <Row>
      {loading && <Loading />}
      {error && <Notification data={{ error }} />}
      {success && <Notification data={{ success }} />}
      <UncontrolledDropdown tag='li' className='dropdown-user nav-item'>
        <DropdownToggle href='/' tag='a' className='nav-link dropdown-user-link' onClick={e => e.preventDefault()}>
          <div className='user-nav d-sm-flex d-none'>
            <span className='user-name font-weight-bold'>{person?.name || (oidcUser && oidcUser.profile?.name) || 'CyberID User'}</span>
            <span className='user-status'>{(oidcUser && oidcUser.profile?.role) || 'User'}</span>
          </div>
          <Avatar img={avatar || defaultAvatar} imgHeight='40' imgWidth='40' status='online' />
        </DropdownToggle>
        <DropdownMenu right>
          <DropdownItem tag='a' onClick={() => openModal()}>
            <Key size={14} className='mr-75' />
            <span className='align-middle'>{intl.formatMessage({ id: "authentication" })}</span>
          </DropdownItem>
          <Modal
            isOpen={modalOTP}
            size="lg"
            className="modal-dialog-centered"
            modalClassName="modal-primary">
            <ModalHeader>
              {intl.formatMessage({ id: "authentication" })}
            </ModalHeader>
            <ModalBody>
              <>
                <div className="d-flex justify-content-center">
                  <Col md="4">
                    <Label className="label mb-1" style={{ fontSize: 16 }}>{intl.formatMessage({ id: "statusActive" })}</Label>

                    <Label className="label" style={{ fontSize: 16 }}>{intl.formatMessage({ id: "devices" })}</Label>
                  </Col>
                  <Col md="8">
                    {devices?.length > 0 ? (
                      <div className='d-flex align-items-center'>
                        <CustomInput
                          type="switch"
                          id="requiredActionsOn"
                          className="custom-control-Primary mb-1 mr-2"
                          checked={true}
                          onChange={() => activeOtp()}
                          disabled
                        />
                        <Label
                          className="label mb-1"
                          style={{
                            fontSize: 16,
                            color: "#0951ff",
                            textDecorationLine: "underline",
                            cursor: 'pointer'
                          }}
                          onClick={() => activeOtp()}
                        >
                          {intl.formatMessage({ id: "add" })}
                        </Label>
                      </div>
                    ) : (
                      <CustomInput
                        type="switch"
                        id="requiredActionsOff"
                        className="custom-control-Primary mb-1"
                        checked={false}
                        onChange={() => activeOtp()}
                      />
                    )}
                    {devices.map((item, index) => {
                      return (
                        <div className='d-grid' key={index} >
                          <Label
                            className="label mb-1 mr-2"
                            style={{
                              fontSize: 16,
                              wordBreak: 'break-word'
                            }}
                          >
                            {item?.userLabel ? item?.userLabel : "unnamed"}
                          </Label>
                          <Label
                            className="label mb-1"
                            style={{
                              fontSize: 16,
                              color: "#ff0000",
                              textDecorationLine: "underline",
                              cursor: 'pointer'
                            }}
                            onClick={() => deleteDevice(item?.id)}
                          >
                            {intl.formatMessage({ id: "delete" })}
                          </Label>
                        </div>
                      )
                    })}
                  </Col>
                </div>
              </>
            </ModalBody>
            <ModalFooter className="justify-content-center">
              <Button.Ripple color='primary' outline type="button" onClick={() => closeModal()}>
                {intl.formatMessage({ id: "close" })}
              </Button.Ripple>
            </ModalFooter>
          </Modal>
          <DropdownItem tag='a' onClick={() => setCenteredModal(!centeredModal)}>
            <RefreshCw size={14} className='mr-75' />
            <span className='align-middle'>{intl.formatMessage({ id: "changePwd" })}</span>
          </DropdownItem>
          <Modal isOpen={centeredModal} toggle={() => setCenteredModal(!centeredModal)} className='modal-dialog-centered' keyboard={false} backdrop="static">
            <ModalHeader toggle={() => setCenteredModal(!centeredModal)} cssModule={{ 'modal-title': 'w-100 text-center' }}><p style={{ color: "#052f95", fontSize: 20 }}>{intl.formatMessage({ id: "changePwd" })}</p></ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label className="label" for="pwdOld">
                  {intl.formatMessage({ id: "pwdOld" })}
                  <span className="text-danger">*</span>
                </Label>
                <div className="pass-wrapper" style={{ position: "relative", display: "flex" }}>
                  <Input
                    style={{ margin: 0 }}
                    type={passwordOldShown ? "text" : "password"}
                    id="pwdOld"
                    name="pwdOld"
                    placeholder={`${intl.formatMessage({ id: "pwdOld" })}`}
                    onChange={(e) =>
                      changePwdValues(
                        e.target.value,
                        "pwdOld"
                      )
                    }
                  >
                  </Input>
                  {passwordOldShown ? <i onClick={togglePasswordOldVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eyeSlash}</i> : <i onClick={togglePasswordOldVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eye}</i>}
                </div>
              </FormGroup>
              <FormGroup>
                <Label className="label" for="pwdNew">
                  {intl.formatMessage({ id: "pwdNew" })}
                  <span style={{ color: 'red' }}>*</span>
                </Label>
                <div className="pass-wrapper" style={{ position: "relative", display: "flex" }}>
                  <Input
                    type={passwordNewShown ? "text" : "password"}
                    id="pwdNew"
                    name="pwdNew"
                    placeholder={`${intl.formatMessage({ id: "pwdNew" })}`}
                    onChange={(e) =>
                      changePwdValues(
                        e.target.value,
                        "pwdNew"
                      )
                    }
                  >
                  </Input>
                  {passwordNewShown ? <i onClick={togglePasswordNewVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eyeSlash}</i> : <i onClick={togglePasswordNewVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eye}</i>}
                </div>
              </FormGroup>
              <FormGroup>
                <Label className="label" for="pwdConfirm">
                  {intl.formatMessage({ id: "pwdConfirm" })}
                  <span style={{ color: 'red' }}>*</span>
                </Label>
                <div className="pass-wrapper" style={{ position: "relative", display: "flex" }}>
                  <Input
                    type={passwordConfirmShown ? "text" : "password"}
                    id="pwdConfirm"
                    name="pwdConfirm"
                    placeholder={`${intl.formatMessage({ id: "pwdConfirm" })}`}
                    onChange={(e) =>
                      changePwdValues(
                        e.target.value,
                        "pwdConfirm"
                      )
                    }
                  >
                  </Input>
                  {passwordConfirmShown ? <i onClick={togglePasswordConfirmVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eyeSlash}</i> : <i onClick={togglePasswordConfirmVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eye}</i>}
                </div>
              </FormGroup>
              <AlertTriangle style={{ color: "#ff9f43" }} size={14} /> {intl.formatMessage({ id: "passwordPolicy" })}
            </ModalBody>
            <ModalFooter className="justify-content-center">
              <Button color='primary' onClick={changePassword}>
                {intl.formatMessage({ id: "save" })}
              </Button>
              <Button.Ripple color='primary' outline type="button" onClick={() => setCenteredModal(!centeredModal)}>
                {intl.formatMessage({ id: "cancel" })}
              </Button.Ripple>
            </ModalFooter>
          </Modal>
          <DropdownItem tag={Link} to='/' onClick={logout}>
            <Power size={14} className='mr-75' />
            <span className='align-middle'>{intl.formatMessage({ id: "logout" })}</span>
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    </Row>
  )
}

export default UserDropdown
